import Bugsnag from '@bugsnag/js';
import blogService from '@services/BlogService';
import clinicService from '@services/ClinicService';
import ProductService from '@services/ProductService';
import promoService from '@services/PromoService';
import { Post } from 'app/types/blog';
import { Clinic } from 'app/types/clinic';
import { Product } from 'app/types/product';
import { Promo } from 'app/types/promo';

import { slimProductForWeb } from './productDto';

export async function fetchProducts() {
  // Esthetic = 1, Medical = 2

  try {
    const fetchedProducts = await ProductService.getAllProducts({});

    if (fetchedProducts && fetchedProducts.length) {
      const products = fetchedProducts.map((product: Product) => ({
        ...slimProductForWeb(product),
        visibility: true,
      }));

      return products;
    }
    return [];
  } catch (error: any) {
    Bugsnag.notify(
      'Error fetching products ' + error + '. Products: ' + fetchProducts.length
    );
  }

  return [] as Product[];
}

// Caché de peticiones en el navegador: una página de producto pedía el mismo
// producto (el del probador virtual) hasta 8 veces, una por cada botón
// "Me interesa" (~54 KB cada una). Solo para la web; el dashboard no cachea.
const clientProductRequests = new Map<string, Promise<Product | undefined>>();

// Google Ads deja a veces su parametro {ignore} sin sustituir pegado al slug
// ("hydrafacial{ignore}", tal cual o codificado), y la API no encontraba el producto.
function cleanProductSlug(slug: string) {
  return slug.replace(/(\{|%7B)ignore(\}|%7D)/gi, '');
}

export async function fetchProduct(rawSlug: string, isDashboard: boolean) {
  const slug = cleanProductSlug(rawSlug ?? '');
  // Sin slug no hay producto que pedir (la API registraba "Product null for ID null").
  if (!slug || slug === 'null' || slug === 'undefined') return undefined;
  const useClientCache = typeof window !== 'undefined' && !isDashboard;

  if (useClientCache && clientProductRequests.has(slug)) {
    return clientProductRequests.get(slug);
  }

  const request = (async () => {
    try {
      const product: Product | undefined = await ProductService.getProduct(
        slug,
        isDashboard
      );

      return product ? slimProductForWeb(product) : product;
    } catch (error: any) {
      Bugsnag.notify('Error fetching product:', error);
    }

    return {} as Product;
  })();

  if (useClientCache) {
    clientProductRequests.set(slug, request);
    // No cachear respuestas vacías (error o producto inexistente).
    request.then(product => {
      if (!product || !(product as Product).id) {
        clientProductRequests.delete(slug);
      }
    });
  }

  return request;
}

export async function fetchClinics() {
  try {
    let clinics: Clinic[] = await clinicService.getClinics();
    clinics = clinics.sort((a, b) => a.order - b.order);
    return clinics;
  } catch (error: any) {
    Bugsnag.notify('Error fetching clinics:', error);
  }

  return [] as Clinic[];
}

export async function fetchPromos() {
  try {
    const promo: Promo = await promoService.getPromos();

    return promo;
  } catch (error: any) {
    Bugsnag.notify('Error fetching promos:', error);
  }

  return {} as Promo;
}

export async function fetchBlogPosts() {
  try {
    const blogPosts: Post[] = await blogService.getBlogPosts();

    return blogPosts;
  } catch (error: any) {
    Bugsnag.notify('Error fetching posts from blog:', error);
  }

  return [] as Post[];
}
